import { motion } from "framer-motion";
import { Zap, ShieldCheck, Scale, PiggyBank, PenLine, HardHat, CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { SiteLang } from "@/hooks/useLanguage";
import commissioningWhatImg from "@/assets/services/qualification-validation/offerings/commissioning-what.jpg";
import commissioningHowImg from "@/assets/services/qualification-validation/offerings/commissioning-how.jpg";

interface Props {
  onCtaClick: () => void;
  lang?: SiteLang;
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" as const },
  }),
};

const i18n = {
  en: {
    whatTitle: "What is commissioning?",
    whatP1: "Commissioning involves <strong class='text-foreground'>a systematic approach to check all components, identify any issues, and make necessary adjustments</strong> to ensure optimal performance.",
    whatP2: "Commissioning ensures that systems, equipment, utilities and facilities operate efficiently and effectively according to their intended design and specifications. Commissioning activities can be performed before it is installed, Factory Acceptance Testing (FAT), and once it is installed, Site Acceptance Testing (SAT), at the site.",
    whatP3: "It includes <strong class='text-foreground'>testing, verifying, and adjusting all components</strong> to ensure they work together smoothly and meet operational needs. Commissioning plays a critical role in achieving operational excellence, maximizing productivity, and minimizing risks.",
    whyTitle: "Why is commissioning important?",
    benefits: [
      { title: "Optimized performance", text: "Commissioning ensures that systems, utilities and equipment operate at their highest efficiency, optimizing productivity while minimizing energy usage." },
      { title: "Reliability and safety", text: "Through precise testing, commissioning identifies and addresses potential issues or defects, enhancing reliability and safety in operation." },
      { title: "Compliance", text: "Commissioning helps ensure that systems and equipment meet regulatory standards and industry best practices, reducing the risk of non-compliance and associated penalties." },
      { title: "Cost savings", text: "By identifying and resolving issues early on, commissioning can help avoid costly repairs, downtime, and inefficiencies over the lifespan of equipment." },
    ],
    howTitle: "How can we help?",
    howIntro: "At QbD Group, we provide comprehensive commissioning support starting from the very first steps. In the <strong class='text-foreground'>Pre-Design Phase</strong>, we work closely with you to define project goals and objectives while meticulously planning and organizing the commissioning process to ensure a seamless execution.",
    phases: [
      { title: "Design phase", text: "Commissioning activities in this phase involve reviewing design documents, specifications, and plans to ensure that commissioning requirements are incorporated into your project design." },
      { title: "Construction phase", text: "This phase includes commissioning activities such as overseeing installation, conducting inspections, and verifying that systems are installed correctly according to design specifications." },
      { title: "Acceptance phase", text: "The acceptance phase involves testing and verifying that systems and equipment meet your performance requirements and operational objectives. Any deficiencies are identified and addressed before final acceptance." },
      { title: "Further qualification phases", text: "If the commissioning is organized, executed and documented in qualitative way, it could be leveraged in further qualification stages, reducing the time and cost needed for the qualification/validation phase." },
    ],
    partnerTitle: "Why partner with QbD Group?",
    partnerIntro: "At QbD Group, we understand that the C&Q phase is critical for the success of your project. Our approach ensures efficiency, cost savings, and smooth execution every step of the way.",
    partnerBullets: [
      "Clear, pragmatic direction and execution are essential for a well-executed C&Q phase.",
      "Our expert support helps reduce overall project costs.",
      "Pragmatism, right-first-time execution, and leverage are at the heart of our approach.",
    ],
    cta: "Contact us",
  },
  es: {
    whatTitle: "¿Qué es el commissioning?",
    whatP1: "El commissioning implica <strong class='text-foreground'>un enfoque sistemático para verificar todos los componentes, identificar cualquier problema y realizar los ajustes necesarios</strong> para garantizar un rendimiento óptimo.",
    whatP2: "El commissioning asegura que los sistemas, equipos, utilities e instalaciones funcionen de manera eficiente y efectiva según su diseño y especificaciones previstos. Las actividades de commissioning pueden realizarse antes de la instalación, pruebas de aceptación en fábrica (FAT), y una vez instalado, pruebas de aceptación en sitio (SAT).",
    whatP3: "Incluye <strong class='text-foreground'>pruebas, verificación y ajuste de todos los componentes</strong> para asegurar que funcionan conjuntamente sin problemas y cumplen las necesidades operativas. El commissioning desempeña un papel fundamental en la consecución de la excelencia operativa, la maximización de la productividad y la minimización de riesgos.",
    whyTitle: "¿Por qué es importante el commissioning?",
    benefits: [
      { title: "Rendimiento optimizado", text: "El commissioning asegura que los sistemas, utilities y equipos operen a su máxima eficiencia, optimizando la productividad y minimizando el consumo energético." },
      { title: "Fiabilidad y seguridad", text: "Mediante pruebas precisas, el commissioning identifica y aborda problemas o defectos potenciales, mejorando la fiabilidad y seguridad en la operación." },
      { title: "Cumplimiento normativo", text: "El commissioning ayuda a asegurar que los sistemas y equipos cumplen los estándares regulatorios y las mejores prácticas del sector, reduciendo el riesgo de incumplimiento." },
      { title: "Ahorro de costes", text: "Al identificar y resolver problemas de forma temprana, el commissioning puede evitar reparaciones costosas, tiempos de inactividad e ineficiencias a lo largo de la vida útil del equipo." },
    ],
    howTitle: "¿Cómo podemos ayudarte?",
    howIntro: "En QbD Group ofrecemos soporte integral de commissioning desde los primeros pasos. En la <strong class='text-foreground'>fase de prediseño</strong>, trabajamos estrechamente contigo para definir los objetivos del proyecto mientras planificamos y organizamos meticulosamente el proceso de commissioning para garantizar una ejecución fluida.",
    phases: [
      { title: "Fase de diseño", text: "Las actividades de commissioning en esta fase implican la revisión de documentos de diseño, especificaciones y planos para asegurar que los requisitos de commissioning se incorporen al diseño del proyecto." },
      { title: "Fase de construcción", text: "Esta fase incluye actividades como la supervisión de la instalación, la realización de inspecciones y la verificación de que los sistemas se instalan correctamente según las especificaciones de diseño." },
      { title: "Fase de aceptación", text: "La fase de aceptación implica probar y verificar que los sistemas y equipos cumplen los requisitos de rendimiento y objetivos operativos. Cualquier deficiencia se identifica y se aborda antes de la aceptación final." },
      { title: "Fases de cualificación posteriores", text: "Si el commissioning está organizado, ejecutado y documentado de forma cualitativa, puede aprovecharse en las etapas de cualificación posteriores, reduciendo el tiempo y el coste de la fase de cualificación/validación." },
    ],
    partnerTitle: "¿Por qué elegir QbD Group?",
    partnerIntro: "En QbD Group entendemos que la fase de C&Q es fundamental para el éxito de tu proyecto. Nuestro enfoque garantiza eficiencia, ahorro de costes y una ejecución fluida en cada etapa.",
    partnerBullets: [
      "Una dirección y ejecución claras y pragmáticas son esenciales para una fase de C&Q bien ejecutada.",
      "Nuestro soporte experto ayuda a reducir los costes globales del proyecto.",
      "El pragmatismo, la ejecución right-first-time y el aprovechamiento están en el corazón de nuestro enfoque.",
    ],
    cta: "Contáctanos",
  },
  "zh-cn": {
    whatTitle: "什么是 commissioning？",
    whatP1: "Commissioning 涉及<strong class='text-foreground'>一种系统化的方法，用于检查所有组件、识别任何问题并进行必要的调整</strong>，以确保最佳性能。",
    whatP2: "Commissioning 确保系统、设备、utilities 和设施按照其预期的设计和规范高效、有效地运行。Commissioning 活动可以在安装前进行（工厂验收测试，FAT），也可以在安装后进行（现场验收测试，SAT）。",
    whatP3: "它包括<strong class='text-foreground'>对所有组件进行测试、验证和调整</strong>，以确保它们能够顺畅地协同工作并满足运营需求。Commissioning 在实现卓越运营、最大化生产力和降低风险方面发挥着关键作用。",
    whyTitle: "为什么 commissioning 很重要？",
    benefits: [
      { title: "性能优化", text: "Commissioning 确保系统、utilities 和设备以最高效率运行，在最大化生产力的同时最大限度地降低能源消耗。" },
      { title: "可靠性与安全性", text: "通过精确的测试，commissioning 能够识别并解决潜在的问题或缺陷，提升运营中的可靠性与安全性。" },
      { title: "合规性", text: "Commissioning 有助于确保系统和设备符合法规标准和行业最佳实践，降低不合规及相关处罚的风险。" },
      { title: "成本节约", text: "通过尽早识别并解决问题，commissioning 可以避免在设备整个使用寿命期间出现昂贵的维修、停机和效率低下的情况。" },
    ],
    howTitle: "我们如何提供帮助？",
    howIntro: "在 QbD Group，我们从最初的阶段开始就提供全面的 commissioning 支持。在<strong class='text-foreground'>预设计阶段</strong>，我们与您紧密合作，明确项目目标，并细致地规划和组织 commissioning 流程，以确保顺畅的执行。",
    phases: [
      { title: "设计阶段", text: "该阶段的 commissioning 活动包括审查设计文件、规范和图纸，以确保 commissioning 要求被纳入您的项目设计中。" },
      { title: "施工阶段", text: "该阶段包括监督安装、开展检查以及验证系统是否按设计规范正确安装等 commissioning 活动。" },
      { title: "验收阶段", text: "验收阶段涉及测试和验证系统及设备是否满足您的性能要求和运营目标。在最终验收之前，任何缺陷都会被识别并加以解决。" },
      { title: "后续确认阶段", text: "如果 commissioning 以高质量的方式进行组织、执行和记录，则可以在后续的确认阶段加以利用，从而减少确认/验证阶段所需的时间和成本。" },
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "在 QbD Group，我们深知 C&Q 阶段对项目成功至关重要。我们的方法在每一个环节都确保效率、成本节约和顺畅执行。",
    partnerBullets: [
      "清晰、务实的指导和执行对于一个出色的 C&Q 阶段至关重要。",
      "我们的专业支持有助于降低项目的整体成本。",
      "务实、一次做对的执行以及充分利用既有成果是我们方法的核心。",
    ],
    cta: "联系我们",
  },
};

const benefitIcons = [Zap, ShieldCheck, Scale, PiggyBank];
const phaseIcons = [PenLine, HardHat, CheckCircle, ArrowRight];

const CommissioningContent = ({ onCtaClick, lang = "en" }: Props) => {
  const t = i18n[lang] ?? i18n.en;

  return (
    <div className="bg-background">
      {/* ── What is commissioning? ── */}
      <section className="py-20 sm:py-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-2xl sm:text-3xl font-bold text-foreground mb-8">
            {t.whatTitle}
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="space-y-5">
              {[t.whatP1, t.whatP2, t.whatP3].map((p, i) => (
                <motion.p key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i + 1} className="text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: p }} />
              ))}
            </div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={2} className="rounded-2xl overflow-hidden">
              <img src={commissioningWhatImg} alt="Commissioning — Qualification and Validation — QbD Group" className="w-full h-auto object-cover" loading="lazy" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Why is commissioning important? ── */}
      <section className="py-20 sm:py-28 bg-secondary">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-2xl sm:text-3xl font-bold text-foreground mb-12">
            {t.whyTitle}
          </motion.h2>
          <div className="grid sm:grid-cols-2 gap-8">
            {t.benefits.map((b, i) => {
              const Icon = benefitIcons[i];
              return (
                <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className="flex items-start gap-4">
                  <Icon className="w-6 h-6 shrink-0 mt-1" style={{ color: "var(--service-color)" }} />
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{b.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{b.text}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── How can we help? ── */}
      <section className="py-20 sm:py-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-2xl sm:text-3xl font-bold text-foreground mb-6">
            {t.howTitle}
          </motion.h2>
          <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1} className="text-muted-foreground leading-relaxed mb-12 max-w-3xl" dangerouslySetInnerHTML={{ __html: t.howIntro }} />
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="space-y-8">
              {t.phases.map((p, i) => {
                const Icon = phaseIcons[i];
                return (
                  <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className="flex items-start gap-4">
                    <Icon className="w-6 h-6 shrink-0 mt-1" style={{ color: "var(--service-color)" }} />
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">{p.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{p.text}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={2} className="rounded-2xl overflow-hidden">
              <img src={commissioningHowImg} alt="Commissioning team — QbD Group" className="w-full h-auto object-cover" loading="lazy" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Why partner with QbD Group? ── */}
      <section className="py-20 sm:py-28 bg-secondary">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-2xl sm:text-3xl font-bold text-foreground mb-6">
            {t.partnerTitle}
          </motion.h2>
          <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1} className="text-muted-foreground leading-relaxed mb-8 max-w-3xl">
            {t.partnerIntro}
          </motion.p>
          <ul className="space-y-4 mb-10 max-w-3xl">
            {t.partnerBullets.map((bullet, i) => (
              <motion.li key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i + 2} className="flex items-start gap-3 text-muted-foreground leading-relaxed">
                <CheckCircle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "var(--service-color)" }} />
                <span>{bullet}</span>
              </motion.li>
            ))}
          </ul>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={5}>
            <Button onClick={onCtaClick} className="rounded-full">{t.cta}</Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CommissioningContent;
